import { useEffect, useRef, useState } from "react";

/**
 * Returns to attract mode after `timeoutMs` of no touch, reset on `touchend`/`mousemove` — the
 * legacy kiosk's approach, kept. `onIdle` fires at most once per idle period; touching resets it.
 *
 * Returns a `resetCount` that increments on every reset (including the initial arm) so callers
 * can `key` a countdown UI off it and get a fresh animation each time the idle period restarts.
 */
export function useIdleReturn(timeoutMs: number, onIdle: () => void, active: boolean): number {
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  const [resetCount, setResetCount] = useState(0);

  useEffect(() => {
    if (!active) return;

    let timer: ReturnType<typeof setTimeout>;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => onIdleRef.current(), timeoutMs);
      setResetCount((count) => count + 1);
    };

    reset();
    window.addEventListener("touchend", reset);
    window.addEventListener("mousemove", reset);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("touchend", reset);
      window.removeEventListener("mousemove", reset);
    };
  }, [timeoutMs, active]);

  return resetCount;
}
